import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ensureRouteQueryData } from "@/lib/route-loader";
import { Separator } from "@/components/ui/separator";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { CheckIcon, XIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  getOrganizationAdminApplicationGetApplicationDetailQueryOptions,
  useOrganizationAdminApplicationApproveApplication,
  useOrganizationAdminApplicationGetApplicationDetail,
  useOrganizationAdminApplicationRejectApplication,
} from "@/orval/organization-admin-application";
import {
  ApplicationStatus,
  Gender,
  type ApplicationDetailDTO,
  type ApplicationParentDTO,
  type ApplicationStudentDTO,
  type ApproveApplicationDTO,
} from "@/orval/model";
import { findEnumName } from "@/lib/enum-utils";
import { parseApplicationIdParams } from "@/lib/route-params";
import { PortalPageHeader } from "@/components/portal-page-header";

interface ApproveDialogProps {
  application: ApplicationDetailDTO;
  onApprove: (appliationId: number, organizationId: number, data: ApproveApplicationDTO) => void;
  isLoading: boolean;
}

const ApproveDialog = ({ application, onApprove, isLoading }: ApproveDialogProps) => {
  const [selectedParentIds, setSelectedParentIds] = useState<number[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const toggleParent = (id: number) => {
    setSelectedParentIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  };

  const toggleStudent = (id: number) => {
    setSelectedStudentIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  };

  const handleApprove = () => {
    onApprove(application.organizationId, application.id, {
      parentIds: selectedParentIds,
      studentIds: selectedStudentIds,
    });
    setIsOpen(false);
  };

  const resetAndClose = () => {
    setSelectedParentIds([]);
    setSelectedStudentIds([]);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          className="w-full sm:w-auto"
          onClick={() => {
            // Initialize with all parents and students checked
            setSelectedParentIds(application.parents.map((p) => p.id));
            setSelectedStudentIds(application.students.map((s) => s.id));
            setIsOpen(true);
          }}
          disabled={application.status !== ApplicationStatus.Pending}
        >
          <CheckIcon className="mr-2 size-4" /> Approve
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Application</DialogTitle>
          <DialogDescription>Select which family members to approve for this application.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-y-4 py-2 max-h-[60vh] overflow-y-auto">
          <div className="flex flex-col gap-y-2">
            <h3 className="font-medium">Parents/Guardians</h3>
            <Separator />
            {application.parents.map((parent) => (
              <div key={parent.id} className="flex items-center gap-x-2">
                <Checkbox
                  id={`parent-${parent.id}`}
                  checked={selectedParentIds.includes(parent.id)}
                  onCheckedChange={() => toggleParent(parent.id)}
                />
                <label
                  htmlFor={`parent-${parent.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {parent.firstName} {parent.lastName}
                </label>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-y-2">
            <h3 className="font-medium">Students</h3>
            <Separator />
            {application.students.map((student) => (
              <div key={student.id} className="flex items-center gap-x-2">
                <Checkbox
                  id={`student-${student.id}`}
                  checked={selectedStudentIds.includes(student.id)}
                  onCheckedChange={() => toggleStudent(student.id)}
                />
                <label
                  htmlFor={`student-${student.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {student.firstName} {student.lastName} ({student.age} years old)
                </label>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row">
          <Button onClick={handleApprove} disabled={isLoading}>
            Approve Selected
          </Button>
          <Button variant="outline" onClick={resetAndClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function getStatusDisplay(status: ApplicationStatus) {
  if (status === ApplicationStatus.Pending) {
    return {
      variant: "outline",
      description: "Approve or reject this application. This action cannot be undone.",
    } as const;
  }
  if (status === ApplicationStatus.Approved) {
    return { variant: "default", description: "This application has been approved." } as const;
  }
  return { variant: "destructive", description: "This application has been rejected." } as const;
}

const ApplicationDetailPage = () => {
  const { organizationId, applicationId } = Route.useParams();
  const navigate = useNavigate();

  const { data: application, isLoading } = useOrganizationAdminApplicationGetApplicationDetail(
    organizationId,
    applicationId,
  );

  const approveMutation = useOrganizationAdminApplicationApproveApplication();

  const rejectMutation = useOrganizationAdminApplicationRejectApplication();

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col p-4 md:p-6">
        <div className="flex flex-col items-center justify-center gap-y-2">
          <h2 className="text-xl font-medium">Loading…</h2>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="flex flex-1 flex-col p-4 md:p-6">
        <div className="flex flex-col items-center justify-center gap-y-2">
          <h2 className="text-xl font-medium">Application not found</h2>
          <Button
            onClick={() => navigate({ from: Route.fullPath, to: "/organizations/$organizationId/admin/applications" })}
          >
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  const familyName = application.parents[0]?.lastName || application.students[0]?.lastName;
  const applicationLabel = familyName ? `${familyName} Application` : `Application #${application.id}`;
  const statusDisplay = getStatusDisplay(application.status);

  return (
    <div className="p-4 md:p-6">
      <PortalPageHeader
        className="mb-6"
        breadcrumbs={[
          {
            label: "Applications",
            to: "/organizations/$organizationId/admin/applications",
            params: { organizationId },
          },
          { label: applicationLabel },
        ]}
        title={applicationLabel}
        subtitle="Application details"
        actions={<Badge variant={statusDisplay.variant}>{findEnumName(ApplicationStatus, application.status)}</Badge>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="flex flex-col lg:col-span-2 gap-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Primary Information</CardTitle>
            </CardHeader>
            <CardContent className="gap-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Date Applied</p>
                  <p className="font-medium">{format(parseISO(application.createdAt), "MMMM d, yyyy")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Parents/Guardians ({application.parents.length})</CardTitle>
              </CardHeader>
              <CardContent className="gap-y-4">
                {application.parents.map((parent: ApplicationParentDTO) => (
                  <div key={parent.id} className="flex flex-col gap-y-2 border-b pb-2 last:border-0">
                    <p className="font-medium flex items-center">
                      {parent.firstName} {parent.lastName}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm">{parent.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-sm">{parent.phone}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Students ({application.students.length})</CardTitle>
              </CardHeader>
              <CardContent className="gap-y-4">
                {application.students.map((student: ApplicationStudentDTO) => (
                  <div key={student.id} className="flex flex-col gap-y-2 border-b pb-2 last:border-0">
                    <p className="font-medium">
                      {student.firstName} {student.lastName}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Date of Birth</p>
                        <p className="text-sm">{format(parseISO(student.dateOfBirth), "MM/dd/yyyy")}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Age</p>
                        <p className="text-sm">{student.age} years</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Gender</p>
                        <p className="text-sm">{findEnumName(Gender, student.gender)}</p>
                      </div>
                      {student.additionalInformation?.trim() && (
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground">Additional student information / allergies</p>
                          <p className="text-sm whitespace-pre-wrap">{student.additionalInformation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="gap-y-4">
              <p className="text-sm text-muted-foreground">{statusDisplay.description}</p>

              <div className="flex items-center gap-2 pt-2">
                <ApproveDialog
                  application={application}
                  onApprove={(organizationId, applicationId, data) => {
                    approveMutation.mutate({ organizationId, applicationId, data });
                  }}
                  isLoading={approveMutation.isPending}
                />

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                      disabled={application.status !== ApplicationStatus.Pending}
                    >
                      <XIcon className="mr-2 size-4" /> Reject
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. The application will be marked as rejected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row">
                      <AlertDialogAction
                        onClick={() => rejectMutation.mutate({ organizationId, applicationId })}
                        disabled={rejectMutation.isPending}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Reject
                      </AlertDialogAction>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/applications_/$applicationId")({
  params: {
    parse: parseApplicationIdParams,
  },
  loader: ({ params, ...loaderContext }) =>
    ensureRouteQueryData(
      loaderContext,
      getOrganizationAdminApplicationGetApplicationDetailQueryOptions(params.organizationId, params.applicationId),
    ),
  component: ApplicationDetailPage,
});
